import { Bell, CalendarCheck, ChevronRight, Home, Calendar, Film, MapPin, Star, User, Users, Swords } from "lucide-react";
import { PhoneMock } from "../PhoneMock";
import { Wordmark } from "../Wordmark";
import { WhistllLogo } from "../WhistllLogo";

/**
 * App preview: the real Whistll Home screen.
 *
 * Mirrors apps/mobile/src/pages/Home.jsx: mode selector (Book / Find /
 * Compete), Suggested Courts horizontal scroll, 4-item bottom nav
 * (Home / Share / Bookings / Profile).
 *
 * Built in pure CSS/SVG so it scales cleanly until we can export a real
 * screenshot from the mobile app.
 */
export function HomeMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* App header: logomark + wordmark + bell */}
      <div className="flex items-center justify-between px-4 pt-3 pb-3">
        <div className="flex items-center gap-1.5">
          <WhistllLogo size={18} color="#FF6200" />
          <Wordmark size="text-sm" className="text-[#F5F5DC] tracking-[0.15em]" />
        </div>
        <div className="relative flex h-7 w-7 items-center justify-center rounded-xl">
          <Bell className="h-4 w-4 text-[#8899A6]" />
          <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#FF6200]" />
        </div>
      </div>

      {/* Hero copy */}
      <div className="px-4 pb-4">
        <h1 className="text-[18px] font-black leading-tight tracking-tight text-[#F5F5DC]">
          What are you <span className="text-[#FF6200]">running</span> today?
        </h1>
        <p className="mt-1 text-[10px] text-[#8899A6]">
          Book a court, compete, or join a pickup game near you
        </p>
      </div>

      {/* Mode selector: Book / Find / Compete */}
      <div className="grid grid-cols-3 gap-2 px-4 pb-5">
        <ModeCard
          icon={<CalendarCheck className="h-4 w-4" />}
          label="Book a Court"
          accent="#10B981"
        />
        <ModeCard
          icon={<Users className="h-4 w-4" />}
          label="Find a Run"
          accent="#8B5CF6"
        />
        <ModeCard
          icon={<Swords className="h-4 w-4" />}
          label="Compete"
          accent="#06B6D4"
        />
      </div>

      {/* Suggested Courts */}
      <div className="px-4">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-black text-[#F5F5DC]">
              Suggested Courts for You
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-[9px] text-[#8899A6]">
              <MapPin className="h-2.5 w-2.5" /> Near Brixton
            </p>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-[#4a5568]" />
        </div>

        {/* Horizontal scroll: show one full card and a sliver of the next */}
        <div className="-mr-4 flex gap-2.5 overflow-hidden pr-4">
          <VenueMiniCard
            name="Brixton Rec"
            address="Brixton, London"
            rating={4.8}
            tags={["Indoor", "Parking"]}
          />
          <VenueMiniCard
            name="Westway Sports"
            address="West London"
            rating={4.6}
            tags={["Outdoor"]}
            truncated
          />
        </div>
      </div>

      {/* Bottom nav: Home / Share / Bookings / Profile */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-around border-t border-[#1e2832] bg-[#0d1117]/90 px-3 py-2 backdrop-blur">
        <NavIcon icon={<Home className="h-4 w-4" />} label="Home" active />
        <NavIcon icon={<Film className="h-4 w-4" />} label="Share" />
        <NavIcon icon={<Calendar className="h-4 w-4" />} label="Bookings" />
        <NavIcon icon={<User className="h-4 w-4" />} label="Profile" />
      </div>
    </PhoneMock>
  );
}

function ModeCard({
  icon,
  label,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  accent: string;
}) {
  return (
    <div
      className="relative flex flex-col items-center gap-2 rounded-2xl p-3"
      style={{
        background: "#131a20",
        border: `1px solid ${accent}55`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <div
        className="flex h-8 w-8 items-center justify-center rounded-xl"
        style={{
          background: `${accent}1a`,
          border: `1px solid ${accent}33`,
          color: accent,
        }}
      >
        {icon}
      </div>
      <span className="text-center text-[9px] font-black leading-tight text-[#F5F5DC]">
        {label}
      </span>
    </div>
  );
}

function VenueMiniCard({
  name,
  address,
  rating,
  tags,
  truncated,
}: {
  name: string;
  address: string;
  rating: number;
  tags: string[];
  truncated?: boolean;
}) {
  return (
    <div
      className={
        "flex-shrink-0 overflow-hidden rounded-2xl border border-[#1e2832] bg-[#131a20] " +
        (truncated ? "w-[140px]" : "w-[180px]")
      }
    >
      {/* Image area */}
      <div
        className="relative h-[90px] w-full"
        style={{
          backgroundImage:
            "linear-gradient(135deg, #1a2a3a 0%, #0d1117 40%, #1e2832 100%)",
        }}
      >
        {/* Subtle court lines */}
        <svg
          className="absolute inset-0 h-full w-full opacity-25"
          viewBox="0 0 180 90"
          preserveAspectRatio="none"
          aria-hidden
        >
          <rect
            x="20"
            y="15"
            width="140"
            height="60"
            fill="none"
            stroke="#FF8C42"
            strokeWidth="1"
          />
          <circle
            cx="90"
            cy="45"
            r="14"
            fill="none"
            stroke="#FF8C42"
            strokeWidth="1"
          />
        </svg>
        {/* Rating pill */}
        <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-bold text-[#FF6200]">
          <Star className="h-2.5 w-2.5 fill-current" />
          {rating.toFixed(1)}
        </div>
      </div>
      {/* Info */}
      <div className="p-2.5">
        <div className="flex items-start justify-between gap-1">
          <p className="truncate text-[10px] font-bold text-[#F5F5DC]">
            {name}
          </p>
          <ChevronRight className="h-3 w-3 shrink-0 text-[#4a5568]" />
        </div>
        <div className="mt-1 flex items-center gap-1 text-[8px] text-[#8899A6]">
          <MapPin className="h-2 w-2" />
          <span className="truncate">{address}</span>
        </div>
        {!truncated && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-[#FF6200]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#FF8C42]"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NavIcon({
  icon,
  label,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div style={{ color: active ? "#FF6200" : "#4a5568" }}>{icon}</div>
      <span
        className={
          "text-[8px] font-semibold " +
          (active ? "text-[#FF6200]" : "text-[#4a5568]")
        }
      >
        {label}
      </span>
    </div>
  );
}
